<template>
  <InputComponent
    :label="label"
    :sublabel="$attrs.sublabel"
    :class="`field-dynamic field-dynamic--theme-${options.theme}`"
  >
    <div class="elder-input__element field-dynamic__items" :style="style">
      <Field
        v-for="(item, index) in items"
        :key="index"
        :value="get(item.key)"
        :schema="item"
        class="field-dynamic__item"
        :style="{ '--grid-area': item.key }"
        v-on="{
          ...$listeners,
          input: $ev => onInput($ev, item),
          remove: $ev => $emit('remove', $ev),
        }"
      />
    </div>
  </InputComponent>
</template>

<script>
import { InputComponent } from 'vue-elder-input'
import { assocPath, path } from 'ramda'
import { RemoveBrackets, isList } from '../utils'
import { Query } from 'mingo'

export default {
  props: {
    config: Object,
    options: Object,
    label: String,
  },
  inject: ['injection'],
  watch: {
    '$attrs.value': {
      handler(val) {
        this.value = val || {}
      },
      immediate: true,
    },
  },
  data() {
    return {
      value: {},
    }
  },
  computed: {
    data() {
      return this.injection.data
    },
    style() {
      let layout = this.options && this.options.layout
      if (!layout) return {}

      let rows = layout.split('|')

      let cols = rows[0]
        .split(' ')
        .map(col => col.trim())
        .filter(Boolean)
        .map(col => {
          let [, cols = '1fr'] = col.split(':')
          return cols
        })
        .join(' ')
      let areas = rows
        .map(row =>
          row
            .split(' ')
            .map(col => col.trim())
            .filter(Boolean)
            .map((col, index, arr) => {
              let [key] = col.split(':')
              if (!this.isEnabled(key)) return this.getReplacement(key, arr)
              return key
            })
            .filter(Boolean)
            .join(' '),
        )
        .filter(Boolean)
        .map(r => `"${r}"`)
        .join('\n')

      return {
        '--grid-template-areas': areas,
        '--grid-template-columns': cols,
      }
    },
    items() {
      const getSubcomponents = () => {
        if (typeof this.config.subcomponents === 'string')
          return RemoveBrackets(this.config.subcomponents)
            .split(',')
            .map(e => {
              let [component, key, label] = e.split(':')
              return { component, key, label, isList: isList(component) }
            })
        if (this.config.subcomponents instanceof Array)
          return this.config.subcomponents.map(e => {
            return {
              ...e,
              isList: isList(e),
            }
          })
        return []
      }

      let components = getSubcomponents()

      return components.map(componentConfig => {
        const { component, key, label, isList, options = {} } = componentConfig

        let result = {
          component,
          isList,
          label,
          key,
          parentIsList: this.config.isList,
          parentKey: this.config.parentKey,
          parentIndex: this.config.index,
          options,
        }

        let props = path(['props', key], this.options)
        let validation = path(['validation', key], this.options)
        let condition = this.getCondition(key)
        if (props) result.options.props = props
        if (validation) result.options.validation = validation
        if (condition) result.options.condition = condition

        return result
      })
    },
  },
  methods: {
    onInput(val, item) {
      this.value = assocPath(item.key.split('.'), val, this.value || {})
      this.$emit('input', this.value)
    },
    get(key) {
      return path(key.split('.'), this.value)
    },
    listConditionBuilder(condition) {
      return Object.fromEntries(
        Object.entries(condition).map(([key, value]) => {
          if (value instanceof Array) {
            return [
              key,
              value.map(element => {
                return this.listConditionBuilder(element)
              }),
            ]
          }

          key = key.replace(
            new RegExp(`${this.config.parentKey}\.`, 'g'),
            `${this.config.parentKey}.${this.config.index}.`,
          )
          return [key, value]
        }),
      )
    },
    getCondition(key) {
      let condition = path(['condition', key], this.options)
      if (!condition) return path(['condition', '*'], this.options)

      if (!this.config.isList && !this.config.parentIsList) return condition

      return this.listConditionBuilder(condition)
    },
    isEnabled(key) {
      let condition = this.getCondition(key)
      if (!condition) return true
      return new Query(condition).test(this.data)
    },
    getReplacement(key, row, direction = -1, isReverseCall) {
      let index = row.indexOf(key)
      let arr = direction > 0 ? row.slice(index) : row.slice(0, index).reverse()
      let target = arr.find(t => this.isEnabled(t))

      return !target && !isReverseCall
        ? this.getReplacement(key, row, direction * -1, true)
        : target
    },
  },
  components: {
    InputComponent,
    Field: () => import('../Field.vue'),
  },
}
</script>

<style lang="scss">
.field-dynamic {
  &__items {
    display: grid;
    gap: 1rem;

    grid-template-areas: var(--grid-template-areas);
    grid-template-columns: var(--grid-template-columns);
  }

  &__item {
    display: flex;
    flex-direction: column;
    grid-area: var(--grid-area);

    .field-renderer__collapsible-header {
      align-self: stretch;
    }
  }

  .kvass-media-droparea {
    min-height: initial !important;
  }

  .kvass-media--outside {
    .kvass-media-droparea {
      min-height: 200px !important;
    }
  }

  &--theme-clean {
    .elder-input__field:first-of-type {
      border: none;
    }

    .field-renderer--number {
      .elder-input__field {
        border: 1px solid var(--vue-elder-border-color, #eaeaea);
      }
    }

    .field-dynamic__items {
      padding: 0;
    }

    .field-renderer--choice {
      .elder-input__field {
        border: none;
      }
    }
  }
}
</style>
